import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import "../styles/Navbar.css";

// Plan helpers
const ACTIVE_SUB_STATUSES = ["active", "trialing", "past_due"];
const OUTREACH_PLANS = ["outreach"];

function hasOutreachPlan(subscription) {
  if (!subscription) return false;
  const status = (subscription.status || "").toLowerCase();
  const name = (subscription.name || "").toLowerCase();
  return ACTIVE_SUB_STATUSES.includes(status) && OUTREACH_PLANS.includes(name);
}

export default function Navbar() {
  const { user, subscription, loading, logout } = useAuth();

  const subStatus = (subscription?.status || "").toLowerCase();
  const isUpgraded = !!user && ACTIVE_SUB_STATUSES.includes(subStatus);
  const outreachAccess = hasOutreachPlan(subscription);
  const freeAccess = !!user; // Free users can access all features

  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const homeHref = freeAccess ? "/search" : "/";

  // Close menu when route changes
  useEffect(() => setOpen(false), [loc.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className="navbar nav" role="banner">
        <div className="nav-inner">
          <div className="nav-left">
            <Link to={homeHref} className="nav-brand">
              <img
                className="brand-icon"
                src="https://www.musicbase.io/logo.png"
                alt=""
                style={{ height: "25px", width: "25px" }}
              />
              <span className="brand-text">MusicBase</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="nav-menu">
              {!isUpgraded && (
                <NavLink to="/pricing" className={({ isActive }) => "nav-link" + (isActive ? " is-active" : "")}>
                  <img src="https://icons.iconarchive.com/icons/iconsmind/outline/512/Pricing-icon.png" alt="" />
                  <span>Pricing</span>
                </NavLink>
              )}
              {user && (
                <NavLink to="/search" className={({ isActive }) => "nav-link" + (isActive ? " is-active" : "")}>
                  <img src="https://cdn-icons-png.flaticon.com/512/54/54481.png" alt="" />
                  <span>Search</span>
                </NavLink>
              )}
              {user && (
                <NavLink to="/campaign" className={({ isActive }) => "nav-link" + (isActive ? " is-active" : "")}>
                  <img src="https://static.thenounproject.com/png/152855-200.png" alt="" />
                  <span>Campaign</span>
                </NavLink>
              )}
            </nav>
          </div>

          <div className="nav-right">
            {/* Burger Button */}
            <button
              className="nav-burger"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <span className="burger-bar" />
              <span className="burger-bar" />
              <span className="burger-bar" />
            </button>

            {/* Desktop Actions */}
            {loading ? (
              <div className="nav-skeleton" style={{ width: 160, height: 36 }} />
            ) : user ? (
              <>
                <Link to="/profile" className="nav-btn ghost">
                  <img src="https://i.imgur.com/0IP7la7.png" alt="" />
                </Link>
                <button onClick={logout} className="nav-btn danger">
                  <img src="https://cdn-icons-png.flaticon.com/512/56/56805.png" alt="" />
                  <span>Log out</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-login-link">Log in</Link>
                <Link to="/signup" className="nav-btn primary"><span>Sign up</span></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* --- MOBILE DRAWER COMPONENTS --- */}
      
      {/* 1. Dark Blur Overlay (Clicking this closes menu) */}
      <div 
        className={`mobile-overlay ${open ? "open" : ""}`} 
        onClick={() => setOpen(false)}
      />

      {/* 2. Side Panel (320px width) */}
      <div className={`mobile-panel ${open ? "open" : ""}`}>
        <div className="mobile-panel-inner">
          
          <div className="mobile-head">
            <Link to={homeHref} className="nav-brand" onClick={() => setOpen(false)}>
              <span className="brand-text">Menu</span>
            </Link>
            <button className="mobile-x" onClick={() => setOpen(false)} aria-label="Close">
               {/* Clean SVG Cross */}
               <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
          </div>

          <nav className="mobile-links">
            {!user && (
              <NavLink to="/" end className="mobile-link" onClick={() => setOpen(false)}>
                Home
              </NavLink>
            )}

            {!isUpgraded && (
              <NavLink to="/pricing" className="mobile-link" onClick={() => setOpen(false)}>
                Pricing
              </NavLink>
            )}

            {user && (
              <NavLink to="/search" className="mobile-link" onClick={() => setOpen(false)}>
                Search
              </NavLink>
            )}

            {user && (
              <NavLink to="/campaign" className="mobile-link" onClick={() => setOpen(false)}>
                Campaign
              </NavLink>
            )}
          </nav>

          <div className="mobile-actions">
            {loading ? null : user ? (
              <>
                <Link to="/profile" className="nav-btn ghost w-full" onClick={() => setOpen(false)}>
                  My Profile
                </Link>
                <button onClick={() => { logout(); setOpen(false); }} className="nav-btn danger w-full">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="mobile-login-link" onClick={() => setOpen(false)}>
                  Sign In
                </Link>
                <Link to="/signup" className="nav-btn primary w-full" onClick={() => setOpen(false)}>
                  <span>Sign Up</span>
                </Link>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
}